import { apiFetch } from './apiClient';

export interface TelegramConfig {
  botToken: string;
  chatId: string;
  enabled: boolean;
}

export interface NotificationLogEntry {
  id: string;
  timestamp: string;
  event: string; // Mapped from eventType
  guestName: string;
  roomName: string; // Mapped from roomId
  status: 'sent' | 'simulated' | 'failed';
  messageContent?: string | null;
  errorMessage?: string | null;
}

export interface NotificationTemplate {
  id: string;
  eventType: string;
  content: string;
  isActive: boolean;
  updatedAt: string;
}

interface PaginatedLogs {
  data: Array<{
    id: string;
    timestamp: string;
    eventType: string;
    guestName: string | null;
    roomId: string | null;
    status: 'sent' | 'simulated' | 'failed';
    messageContent: string | null;
    errorMessage: string | null;
  }>;
  total: number;
}

/**
 * Lấy cấu hình Telegram bot (token, chat ID, và trạng thái kích hoạt) từ backend
 */
export async function getConfig(): Promise<TelegramConfig> {
  return apiFetch<TelegramConfig>('/telegram/config');
}

/**
 * Lưu cấu hình Telegram bot vào backend
 */
export async function saveConfig(config: Partial<TelegramConfig>): Promise<TelegramConfig> {
  return apiFetch<TelegramConfig>('/telegram/config', {
    method: 'PUT',
    body: config,
  });
}

/**
 * Lấy danh sách mẫu tin nhắn thông báo hiện tại
 */
export async function getTemplates(): Promise<NotificationTemplate[]> {
  return apiFetch<NotificationTemplate[]>('/telegram/templates');
}

/**
 * Lưu mẫu tin nhắn thông báo tuỳ chỉnh theo loại sự kiện
 */
export async function saveTemplate(eventType: string, content: string, isActive = true): Promise<NotificationTemplate> {
  return apiFetch<NotificationTemplate>(`/telegram/templates/${eventType}`, {
    method: 'PUT',
    body: { content, isActive },
  });
}

/**
 * Lấy nhật ký thông báo đã gửi/mô phỏng từ backend có phân trang
 */
export async function getLog(page = 1, limit = 20): Promise<{ data: NotificationLogEntry[]; total: number }> {
  const res = await apiFetch<PaginatedLogs>(`/telegram/log?page=${page}&limit=${limit}`);
  
  const mappedData: NotificationLogEntry[] = res.data.map(entry => ({
    id: entry.id,
    timestamp: entry.timestamp,
    event: entry.eventType,
    guestName: entry.guestName || 'N/A',
    roomName: entry.roomId || 'N/A', // Bán thành roomName để tương thích UI cũ
    status: entry.status,
    messageContent: entry.messageContent,
    errorMessage: entry.errorMessage,
  }));

  return {
    data: mappedData,
    total: res.total,
  };
}

/**
 * Gửi thông báo Telegram cho một booking
 * (Hiện tại đã được xử lý tự động ở Backend, giữ hàm này rỗng để tương thích ngược)
 */
export function notify(
  _booking: any,
  _event: string,
  _roomName: string,
): void {
  // Đã được xử lý tự động ở Backend
}

/**
 * Gửi tin nhắn thử nghiệm để kiểm tra cấu hình Telegram
 */
export async function sendTest(): Promise<{ status: string; message: string }> {
  return apiFetch<{ status: string; message: string }>('/telegram/test', {
    method: 'POST',
  });
}

/**
 * Khởi tạo cấu hình Telegram (không làm gì vì lưu ở database backend)
 */
export function init(): void {
  // Không làm gì vì đã sử dụng backend
}

